import {
  messages,
  TwilioCheckCodeSendValues,
  TwilioVerificationChannelEnum,
  UserRoleIDEnum,
  useTwilioCheckCodeMutation,
} from '@alluradirect/react';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';

import Spacer from 'components/core/Spacer/Spacer';
import { useListPageModalNavigation } from 'components/views/pages/list/modal/hooks/useListPageModalNavigation';

import { VerifyCodeInput } from 'components/views/verification/components/VerifyCodeInput';
import { listPageEmailVerification } from 'config/imageUrls';
import { Form, Formik } from 'formik';
import { generateUUID } from 'helpers/cryptoHelpers';
import React from 'react';
import { useIsMutating } from 'react-query';
import * as yup from 'yup';
import { DividerTemp } from '../../components/styles';
import { useListPropertyCreateMutation } from '../hooks/useListPropertyCreate';
import { useListModalContext } from '../listModalContext';
import {
  ListPageSendVerificationButton,
  listSendTwilioVerificationMutationKey,
} from './ListPageSendVerificationButton';

export const ListPageEmailVerificationForm = () => {
  const {
    listFormValues: { email, firstName, lastName },
  } = useListModalContext();
  const [codeInputKey, setCodeInputKey] = React.useState(['codeInputKey', 0]);
  const { pushNextStep } = useListPageModalNavigation();

  const handleCreateProperty = useListPropertyCreateMutation();

  const checkCodeMutation = useTwilioCheckCodeMutation();

  const onSubmit = ({ code }: { code: string }) => {
    // Incredible hack to give the user a temporary password
    const oneTimePassword = `A${generateUUID().toString()}a`;

    const body: TwilioCheckCodeSendValues = {
      value: email,
      channel: TwilioVerificationChannelEnum.email,
      code,
      first_name: firstName,
      last_name: lastName,
      password: oneTimePassword,
      user_type_id: UserRoleIDEnum.owner,
      auto_login_user: true,
    };
    // Review on success method for detailed flow
    checkCodeMutation.mutate(body, {
      onSuccess: () => {
        pushNextStep();
      },
    });
  };

  const isMutating = useIsMutating();
  const isSendVerificationMutating = useIsMutating(listSendTwilioVerificationMutationKey);

  const isLoading = handleCreateProperty.isLoading || isMutating > 0;

  return (
    <>
      <Box textAlign="center">
        <img src={listPageEmailVerification} width="120px" />
        <DividerTemp my={3} />

        <Typography variant="h5" mb={2}>
          Please verify your email
        </Typography>
      </Box>
      <DividerTemp my={3} />
      <Typography mb={2}>
        We have emailed <strong>{email}</strong> a 6 digit code. Please enter this code below to validate your email.
      </Typography>
      <Formik
        initialValues={{ code: '' }}
        onSubmit={onSubmit}
        validationSchema={yup.object({ code: yup.string().required(messages.requiredValue) })}
      >
        <Form>
          <VerifyCodeInput key={codeInputKey.join('')} />
          <DividerTemp my={3} />

          <LoadingButton
            isFormikButton
            variant="contained"
            buttonColor="secondary"
            isFullWidth
            size="large"
            // We do not want this loading state to trigger when we resend the verification code. This would result in 2 loading buttons which is ugly.
            isLoading={isSendVerificationMutating ? false : isLoading}
          >
            Verify and Continue
          </LoadingButton>
          <Spacer />
          <ListPageSendVerificationButton
            text="Resend Verification"
            onClick={() => setCodeInputKey(prev => [prev[0], Number(prev[1]) + 1])}
          />
        </Form>
      </Formik>
    </>
  );
};
